import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {useToast} from './use-toast'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST_DESCRIPTION,
    TOAST_MESSAGE_GO_TO_WISHLIST,
    API_ERROR_MESSAGE,
} from '../../app/constants'
import {Button} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'

export const useWishlistActions = (wishlist) => {
    const [wishlistLoading, setWishlistLoading] = useState([])
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem',
    )
    const {data: customer} = useCurrentCustomer()
    const customerId = useCustomerId()
    const {formatMessage} = useIntl()
    const showFeedback = useToast()

    const addItemToWishlist = async (product) => {
        const productId = product?.productId ? product?.productId : product?.c_subModelIdentifier
        setWishlistLoading([...wishlistLoading, productId])

        const listId = wishlist?.id
        await createCustomerProductListItem(
            {
                parameters: {customerId, listId},
                body: {
                    quantity: 1,
                    public: true,
                    priority: 1,
                    type: 'product',
                    productId: productId,
                },
            },
            {
                onError: () => {
                    showFeedback({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        variant: 'default',
                    })
                },
                onSuccess: () => {
                    showFeedback({
                        title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                        status: 'success',
                        variant: 'default',
                        position: 'top-right',
                        duration: 5000,
                        isClosable: !customer?.isGuest,
                        description: customer?.isGuest
                            ? formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST_DESCRIPTION)
                            : '',
                        action: customer?.isGuest ? (
                            <Button as={Link} to="/wishlist" variant="outline-not-rounded" w="100%">
                                {formatMessage(TOAST_MESSAGE_GO_TO_WISHLIST)}
                            </Button>
                        ) : null,
                    })
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== productId))
                },
            },
        )
    }

    const removeItemFromWishlist = async (product) => {
        const productId = product?.productId ? product?.productId : product?.c_subModelIdentifier
        setWishlistLoading([...wishlistLoading, productId])

        const listId = wishlist?.id
        const itemId = wishlist?.customerProductListItems.find((i) => i.productId === productId).id

        await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId},
            },
            {
                onError: () => {
                    showFeedback({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        variant: 'default',
                    })
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== productId))
                },
            },
        )
    }
    const isProductInWishlist = (productId) => {
        return !!wishlist?.customerProductListItems?.find((item) => item.productId === productId)
    }
    const toggleWishlistItem = async (product, isFavouriteLoading) => {
        if (isFavouriteLoading.current) return
        isFavouriteLoading.current = true
        const productId = product?.productId ? product?.productId : product?.c_subModelIdentifier
        const isInWishlist = isProductInWishlist(productId)
        const action = isInWishlist ? removeItemFromWishlist : addItemToWishlist

        await action(product)
        isFavouriteLoading.current = false
    }

    return {
        wishlistLoading,
        addItemToWishlist,
        removeItemFromWishlist,
        isProductInWishlist,
        toggleWishlistItem,
    }
}
